import { useQuery } from '@tanstack/react-query';

import { FIVE_MINUTES_IN_MS } from '../services/ExternalService/constants';
import ExternalService from '../services/ExternalService/ExternalApiService';
import { type IDPConfig, type IDPConfigKosmos } from '../services/types';

interface ErrorResponse {
  message?: string;
  errorMessage?: string;
}

export class ApiError extends Error {
  status: number;

  errorMessage: string;

  constructor(message: string, status: number, errorMessage: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorMessage = errorMessage;
    Object.setPrototypeOf(this, ApiError.prototype); // Ensure proper inheritance
  }
}
interface ApiResponse {
  success: boolean; // Indicates if the API call was successful
  errorMessage: string; // Optional message from the server
  data: IDPConfig[] | IDPConfigKosmos[]; // The actual data returned by the API
  error: ErrorResponse; // Error details in case of failure
  status: number;
  ok?: boolean;
}

/**
 * Fetches the list of ID Proofing providers from the external API service.
 *
 * @async
 * @function fetchIDPProviders
 * @returns {Promise<IDPConfig[] | IDPConfigKosmos[]>} A promise that resolves to an array of ID Proofing providers.
 */
const fetchIDPProviders = async (): Promise<
  IDPConfig[] | IDPConfigKosmos[]
> => {
  const response =
    (await ExternalService.getIDProofingProviders()) as ApiResponse;

  if (!response.data && !response.ok) {
    throw new ApiError(
      `Error ${response.errorMessage}: ${response.status}`,
      response.status,
      response.errorMessage
    );
  }

  return response.data;
};

/**
 * Custom hook to retrieve the list of ID Proofing providers using React Query's `useQuery`.
 *
 * @function useGetIDProviders
 * @returns {import('@tanstack/react-query').UseQueryResult<IDPConfig[] | IDPConfigKosmos[], unknown>} A query result object containing the list of rules, loading state, and potential errors.
 */
const useGetIDProviders = () =>
  useQuery<IDPConfig[] | IDPConfigKosmos[], ApiError>({
    queryKey: ['IDP_PROVIDERS'], // Unique query key
    queryFn: fetchIDPProviders, // Function to fetch data
    staleTime: FIVE_MINUTES_IN_MS, // Cache data for 5 minutes
    retry: 0,
  });

export default useGetIDProviders;
